import React, { RefObject } from 'react';
import { isValidNhsNumber } from '../../utils/nhsValidation';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  FormHelperText,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { fileService } from '../../services/fileService';
import StripeCCField, { StripeCCFieldHandle } from './StripeCCField';
import {
  replaceScorePlaceholders,
  substituteVariables,
  insertAsteriskBeforeList,
  getListStyles,
} from '../../utils/questionnaireFieldUtils';
import { evaluateFieldVisibility } from '../../utils/conditionalLogic';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WizardRenderFieldProps {
  field: any;
  /** Key prefix for the top-level Box (e.g. `"section-0-fieldName"`) */
  fieldKey: string;
  control: Control<Record<string, any>>;
  errors: FieldErrors<Record<string, any>>;
  formValues: Record<string, any>;
  scores: Record<string, any>;
  /** Resolved {variable} values substituted into content fields */
  variableValues?: Record<string, string>;
  isMobile: boolean;
  saving: boolean;

  // ── optional/wizard-specific ──────────────────────────────────────────────
  /** Public questionnaire token (public wizard only) */
  token?: string | null;
  /** Questionnaire ID used for authenticated file uploads (generic wizard only) */
  questionnaireId?: number | null;
  /** Payment amount in cents for the summary-box display */
  paymentAmount?: number;
  /** Configured payment amount (in pence) from the questionnaire's payment_config */
  paymentConfigAmount?: number;
  /** Per-referral discount copy shown under the total in the summary box */
  paymentDiscountText?: string;
  /** Label for the Stripe pay button (amount is prefixed automatically) */
  paymentButtonLabel?: string;
  /** Ref to the Stripe CC field imperative handle */
  stripeCCFieldRef?: RefObject<StripeCCFieldHandle>;

  // ── callbacks ─────────────────────────────────────────────────────────────
  onOpenPopup: (popupId: string) => void;
  onSubmit: () => void;
  /**
   * Called when a 'next' action button is clicked.
   *
   * Public wizard: maps to `handleNextSection(skipNavigation?)`
   * Generic wizard: maps to `handleNext(shouldCalculateScore?, scoreName?, skipNavigation?)`
   */
  onNext: (opts?: {
    skipNavigation?: boolean;
    shouldCalculateScore?: boolean;
    scoreName?: string;
  }) => Promise<boolean | void>;
  /**
   * Called when an 'exit' action button is clicked.
   * exitUrl is only used by the public wizard.
   */
  onExit: (exitUrl?: string) => void;
  /**
   * Try to show the exit popup. Return true if popup was shown, false if not.
   * Caller can then fallback to a direct exit.
   */
  onTriggerExitPopup: () => boolean;
  /** Save the current page (pagination-next action) */
  onSaveCurrentPage: () => Promise<void | boolean>;
  /** Mobile: validate current page fields and move to next page */
  onMobileNextPage: () => Promise<void>;
  /** Mobile: returns true if there are more pages after the current one */
  hasMorePages: () => boolean;
  /** Mobile: returns true if the open-popup button should validate before opening */
  shouldValidateBeforePopup?: boolean;
  /** Mobile: trigger field validation */
  onTriggerValidation: (fieldNames: string[]) => Promise<boolean>;
  /** Mobile: scroll container to top */
  onScrollToTop: () => void;
  /** Scroll to first error element */
  onScrollToFirstError?: () => void;
  /** Current section fields (for the open-popup validation) */
  currentSectionFields?: any[];

  // ── Stripe-specific ───────────────────────────────────────────────────────
  onPaymentReady?: (ready: boolean) => void;
  onPaymentSuccess?: () => void;

  // ── button-group popup context ────────────────────────────────────────────
  onSetButtonGroupPopupContext: (ctx: { fieldName: string; resetOnBack: boolean } | null) => void;
  onSetNavigateAfterPopup: (value: boolean) => void;

  // ── inject-custom-icons helper (passed in to avoid re-creating it) ────────
  injectCustomIcons: (html: string, icons: string[]) => string;

  /** Language code — 'he' enables RTL and Hebrew error messages */
  language?: string;
  /** Clinic state — 'IL' or 'UK', used for patient-field-mapped validation */
  clinicState?: string | null;
}

// ─── Translations ─────────────────────────────────────────────────────────────

const buildMessages = (isHebrew: boolean) => ({
  required: isHebrew ? 'שדה חובה' : 'This field is required',
  invalidFormat: isHebrew ? 'פורמט לא תקין' : 'Invalid format',
  invalidDate: isHebrew ? 'יש להזין תאריך תקין' : 'Please enter a valid date',
  datePast: isHebrew ? 'התאריך חייב להיות בעבר' : 'Date must be in the past',
  dateFuture: isHebrew ? 'התאריך חייב להיות בעתיד' : 'Date must be in the future',
  consent: isHebrew ? 'יש לאשר הסכמה כדי להמשיך' : 'You must provide your consent to continue.',
  optional: isHebrew ? '(אופציונלי)' : '(Optional)',
  completePrevious: isHebrew ? 'יש למלא את השאלות הקודמות לפני התשלום' : 'Please complete the previous questions before making payment',
  uploadFailed: isHebrew ? 'העלאת קובץ נכשלה. נסה שוב.' : 'Failed to upload file. Please try again.',
  uploadMultiFailed: isHebrew ? 'העלאת קבצים נכשלה. נסה שוב.' : 'Failed to upload one or more files. Please try again.',
  downloadFailed: isHebrew ? 'הורדת קובץ נכשלה. נסה שוב.' : 'Failed to download file. Please try again.',
  minLength: (n: number) => isHebrew ? `נדרשים לפחות ${n} תווים` : `Must be at least ${n} characters`,
  maxLength: (n: number) => isHebrew ? `מותר לכל היותר ${n} תווים` : `Must be at most ${n} characters`,
  minValue: (n: number) => isHebrew ? `הערך המינימלי הוא ${n}` : `Value must be at least ${n}`,
  maxValue: (n: number) => isHebrew ? `הערך המקסימלי הוא ${n}` : `Value must be at most ${n}`,
  dateWithinDays: (n: number) => isHebrew ? `התאריך חייב להיות ב-${n} הימים האחרונים` : `Date must be within the last ${n} days`,
  minAge: (n: number) => isHebrew ? `גיל מינימלי: ${n}` : `You must be at least ${n} years old`,
  mustMatch: (name: string) => isHebrew ? `חייב להתאים לשדה ${name}` : `Must match ${name}`,
});

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Shared field renderer for all wizard field types.
 * Extracted from PublicQuestionnaireWizardPage and GenericQuestionnaireWizard.
 */
const WizardRenderField: React.FC<WizardRenderFieldProps> = (props) => {
  const {
    field,
    fieldKey: key,
    control,
    errors,
    formValues,
    scores,
    variableValues,
    isMobile,
    saving,
    token,
    questionnaireId,
    paymentAmount = 0,
    paymentConfigAmount,
    paymentDiscountText,
    paymentButtonLabel,
    stripeCCFieldRef,
    onOpenPopup,
    onSubmit,
    onNext,
    onExit,
    onTriggerExitPopup,
    onSaveCurrentPage,
    onMobileNextPage,
    hasMorePages,
    shouldValidateBeforePopup = true,
    onTriggerValidation,
    // onScrollToTop is passed through the props interface but not used directly in the component body
    // (mobile next-page scrolling is handled by the onMobileNextPage callback)
    onScrollToFirstError,
    currentSectionFields,
    onPaymentReady,
    onPaymentSuccess,
    onSetButtonGroupPopupContext,
    onSetNavigateAfterPopup,
    injectCustomIcons,
    language,
    clinicState,
  } = props;

  const isHebrew = language === 'he';
  const msgs = buildMessages(isHebrew);

  // Tracks that this specific button initiated the current save, so only the
  // clicked button shows an inline spinner (all buttons disable via `saving`).
  const [actionClicked, setActionClicked] = React.useState(false);
  React.useEffect(() => {
    if (!saving && actionClicked) setActionClicked(false);
  }, [saving, actionClicked]);

  // Field-level show/hide logic. When the rule's dependency is unmet, render nothing.
  // RHF default (shouldUnregister: false) preserves the field's last value across remounts.
  const isVisible = React.useMemo(
    () => evaluateFieldVisibility(field.name, formValues, undefined, field.conditionalLogic),
    [field.name, formValues, field.conditionalLogic]
  );
  if (!isVisible) return null;

  const getPatientFieldValidation = (mapping: string): Record<string, any> => {
    switch (mapping) {
      case 'id_number':
        if (clinicState === 'IL') {
          return {
            validate: (v: string) =>
              !v || /^\d{9}$/.test(v) || (isHebrew ? 'תעודת זהות חייבת להכיל בדיוק 9 ספרות' : 'Israeli ID must be exactly 9 digits'),
          };
        }
        if (clinicState === 'UK') {
          return {
            validate: (v: string) =>
              !v || isValidNhsNumber(v) || (isHebrew ? 'מספר NHS לא תקין' : 'Invalid NHS number — please check the number'),
          };
        }
        return {};
      case 'email':
        return {
          validate: (v: string) =>
            !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) || msgs.invalidFormat,
        };
      case 'phone':
        return {
          validate: (v: string) =>
            !v || /^[+\d\s\-().]{7,20}$/.test(v) || msgs.invalidFormat,
        };
      default:
        return {};
    }
  };

  const error = errors[field.name];

  // Helper to render a nested field (for 'line' and 'container' types)
  const renderNested = (nestedField: any) => (
    <WizardRenderField
      {...props}
      key={`nested-${nestedField.name}`}
      field={nestedField}
      fieldKey={`${key}-${nestedField.name}`}
    />
  );

  switch (field.type) {
    case 'text':
    case 'email':
      return (
        <Box key={key} data-field-name={field.name} sx={{ mb: { xs: 1.75, sm: 2.5, md: 3 } }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: '#3A7380',
              fontSize: '0.95rem',
              fontWeight: 500,
              fontFamily: '"Open Sans", sans-serif',
            }}
          >
            {field.label}
            {field.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <Controller
            name={field.name}
            control={control}
            rules={{
              ...(field.required && { required: msgs.required }),
              ...(field.validation?.minLength && {
                minLength: {
                  value: field.validation.minLength,
                  message: field.validation.message || msgs.minLength(field.validation.minLength),
                },
              }),
              ...(field.validation?.maxLength && {
                maxLength: {
                  value: field.validation.maxLength,
                  message: field.validation.message || msgs.maxLength(field.validation.maxLength),
                },
              }),
              ...(field.validation?.pattern && {
                pattern: {
                  value: new RegExp(field.validation.pattern),
                  message: field.validation.message || msgs.invalidFormat,
                },
              }),
              // Built-in email-format validation for email fields (when no custom pattern is set)
              ...(field.type === 'email' && !field.validation?.pattern && {
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: field.validation?.message || msgs.invalidFormat,
                },
              }),
              ...(field.validation?.validationType === 'matchField' && field.validation?.matchFieldName && {
                validate: (value: string) => {
                  const matchValue = formValues[field.validation?.matchFieldName!];
                  if (value !== matchValue) {
                    return field.validation?.message || msgs.mustMatch(field.validation?.matchFieldName);
                  }
                  return true;
                },
              }),
              ...((field.patientFieldMapping || field.leadFieldMapping) &&
                getPatientFieldValidation((field.patientFieldMapping || field.leadFieldMapping) as string)),
            }}
            render={({ field: inputField }) => (
              <TextField
                {...inputField}
                fullWidth
                type={field.type === 'email' ? 'email' : 'text'}
                placeholder={field.placeholder}
                variant="outlined"
                error={!!error}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: error ? '#d32f2f' : '#E0E0E0',
                    },
                    '&:hover fieldset': {
                      borderColor: error ? '#d32f2f' : '#3A7380',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: error ? '#d32f2f' : '#3A7380',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '14px 16px',
                    '&::placeholder': {
                      color: '#B7B7B7',
                      opacity: 1,
                    },
                  },
                }}
              />
            )}
          />
          {error && (
            <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
              {typeof error.message === 'string' ? error.message : msgs.required}
            </Typography>
          )}
        </Box>
      );

    case 'textarea':
      return (
        <Box key={key} data-field-name={field.name} sx={{ mb: { xs: 1.75, sm: 2.5, md: 3 } }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: '#3A7380',
              fontSize: '0.95rem',
              fontWeight: 500,
              fontFamily: '"Open Sans", sans-serif',
            }}
          >
            {field.label}
            {field.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <Controller
            name={field.name}
            control={control}
            rules={{
              ...(field.required && { required: msgs.required }),
              ...(field.validation?.minLength && {
                minLength: {
                  value: field.validation.minLength,
                  message: field.validation.message || msgs.minLength(field.validation.minLength),
                },
              }),
              ...(field.validation?.maxLength && {
                maxLength: {
                  value: field.validation.maxLength,
                  message: field.validation.message || msgs.maxLength(field.validation.maxLength),
                },
              }),
              ...(field.validation?.pattern && {
                pattern: {
                  value: new RegExp(field.validation.pattern),
                  message: field.validation.message || msgs.invalidFormat,
                },
              }),
            }}
            render={({ field: inputField }) => (
              <TextField
                {...inputField}
                fullWidth
                placeholder={field.placeholder}
                multiline
                rows={3}
                variant="outlined"
                error={!!error}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    // Pin multiline content to the top; MUI's default center-alignment
                    // (combined with Tailwind preflight resetting textarea padding/line-height)
                    // pushes the text above the visible box.
                    alignItems: 'flex-start',
                    padding: '12px 14px',
                    '& fieldset': {
                      borderColor: error ? '#d32f2f' : '#E0E0E0',
                    },
                    '&:hover fieldset': {
                      borderColor: error ? '#d32f2f' : '#3A7380',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: error ? '#d32f2f' : '#3A7380',
                    },
                  },
                  '& .MuiInputBase-inputMultiline': {
                    padding: 0,
                    lineHeight: 1.5,
                    boxSizing: 'border-box',
                  },
                }}
              />
            )}
          />
          {error && (
            <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
              {typeof error.message === 'string' ? error.message : msgs.required}
            </Typography>
          )}
        </Box>
      );

    case 'select':
    case 'dropdown':
      return (
        <Box key={key} data-field-name={field.name} sx={{ mb: { xs: 1.75, sm: 2.5, md: 3 } }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: '#3A7380',
              fontSize: '0.95rem',
              fontWeight: 500,
              fontFamily: '"Open Sans", sans-serif',
            }}
          >
            {field.label}
            {field.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <Controller
            name={field.name}
            control={control}
            rules={{
              ...(field.required && { required: msgs.required }),
              ...(field.validation?.minLength && {
                minLength: {
                  value: field.validation.minLength,
                  message: field.validation.message || msgs.minLength(field.validation.minLength),
                },
              }),
              ...(field.validation?.maxLength && {
                maxLength: {
                  value: field.validation.maxLength,
                  message: field.validation.message || msgs.maxLength(field.validation.maxLength),
                },
              }),
              ...(field.validation?.pattern && {
                pattern: {
                  value: new RegExp(field.validation.pattern),
                  message: field.validation.message || msgs.invalidFormat,
                },
              }),
            }}
            render={({ field: inputField }) => (
              <FormControl fullWidth error={!!error}>
                <Select
                  {...inputField}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return field.placeholder ? (
                        <span style={{ color: '#B7B7B7' }}>
                          {field.placeholder}
                        </span>
                      ) : '';
                    }
                    return selected as string;
                  }}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: 'top' as const,
                      horizontal: 'left' as const,
                    },
                    transformOrigin: {
                      vertical: 'top' as const,
                      horizontal: 'left' as const,
                    },
                    PaperProps: {
                      sx: {
                        maxHeight: { xs: '400px', md: '300px' },
                        '& .MuiList-root': {
                          py: { xs: 0, md: 1 },
                        },
                      },
                    },
                    sx: {
                      '& .MuiMenuItem-root': {
                        fontSize: { xs: '1rem', md: '0.875rem' },
                        py: { xs: 1.5, md: 1 },
                        '&.Mui-selected': {
                          backgroundColor: { xs: 'transparent', md: 'rgba(58, 115, 128, 0.08)' },
                          '&::before': {
                            content: { xs: '"●"', md: '""' },
                            marginRight: { xs: '12px', md: 0 },
                            color: '#3A7380',
                            fontSize: '1.2rem',
                          },
                        },
                        '&:not(.Mui-selected)::before': {
                          content: { xs: '"○"', md: '""' },
                          marginRight: { xs: '12px', md: 0 },
                          color: '#E0E0E0',
                          fontSize: '1.2rem',
                        },
                      },
                    },
                  }}
                  sx={{
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: error ? '#d32f2f' : '#E0E0E0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: error ? '#d32f2f' : '#3A7380',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: error ? '#d32f2f' : '#3A7380',
                    },
                    '& .MuiSelect-select': {
                      padding: '14px 16px',
                    },
                  }}
                >
                  {field.options?.map((option: any) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />
          {error && (
            <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
              {typeof error.message === 'string' ? error.message : msgs.required}
            </Typography>
          )}
        </Box>
      );

    case 'radio':
      return (
        <Box key={key} data-field-name={field.name} sx={{ mb: { xs: 1.75, sm: 2.5, md: 3 } }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1.5,
              color: '#3A7380',
              fontSize: '0.95rem',
              fontWeight: 500,
              fontFamily: '"Open Sans", sans-serif',
            }}
          >
            {field.label}
            {field.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <Controller
            name={field.name}
            control={control}
            rules={{
              ...(field.required && { required: msgs.required }),
              ...(field.validation?.minLength && {
                minLength: {
                  value: field.validation.minLength,
                  message: field.validation.message || msgs.minLength(field.validation.minLength),
                },
              }),
              ...(field.validation?.maxLength && {
                maxLength: {
                  value: field.validation.maxLength,
                  message: field.validation.message || msgs.maxLength(field.validation.maxLength),
                },
              }),
              ...(field.validation?.pattern && {
                pattern: {
                  value: new RegExp(field.validation.pattern),
                  message: field.validation.message || msgs.invalidFormat,
                },
              }),
            }}
            render={({ field: inputField }) => (
              <RadioGroup {...inputField}>
                {field.options?.map((option: any) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio sx={{ color: '#3A7380' }} />}
                    label={option.label}
                    sx={{ mb: 0.5 }}
                  />
                ))}
              </RadioGroup>
            )}
          />
          {error && (
            <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
              {typeof error.message === 'string' ? error.message : msgs.required}
            </Typography>
          )}
        </Box>
      );

    case 'checkbox':
      // Single checkbox field
      return (
        <Box key={key} data-field-name={field.name} sx={{ mb: { xs: 1.75, sm: 2.5, md: 3 } }}>
          <Controller
            name={field.name}
            control={control}
            rules={field.required ? {
              validate: (value) => value === true || msgs.consent
            } : {}}
            render={({ field: inputField }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!inputField.value}
                    onChange={(e) => inputField.onChange(e.target.checked)}
                    sx={{ color: '#3A7380' }}
                  />
                }
                label={
                  <span style={{
                    color: error ? '#d32f2f' : '#3A7380',
                    fontSize: '0.9rem',
                    fontFamily: '"Open Sans", sans-serif',
                    letterSpacing: 0
                  }}>
                    {field.label}
                    {!field.required && <span style={{ color: '#3A7380' }}> {msgs.optional}</span>}
                  </span>
                }
              />
            )}
          />
          {error && (
            <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
              {typeof error.message === 'string' ? error.message : msgs.consent}
            </Typography>
          )}
        </Box>
      );

    case 'checkboxes':
    case 'multiselect':
    case 'multi-select':
      // Multiple checkboxes field
      return (
        <Box key={key} data-field-name={field.name} sx={{ mb: { xs: 1.75, sm: 2.5, md: 3 } }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1.5,
              color: '#3A7380',
              fontSize: '0.95rem',
              fontWeight: 500,
              fontFamily: '"Open Sans", sans-serif',
            }}
          >
            {field.label}
            {field.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <Controller
            name={field.name}
            control={control}
            rules={{
              ...(field.required && { required: msgs.required }),
              ...(field.validation?.minLength && {
                minLength: {
                  value: field.validation.minLength,
                  message: field.validation.message || msgs.minLength(field.validation.minLength),
                },
              }),
              ...(field.validation?.maxLength && {
                maxLength: {
                  value: field.validation.maxLength,
                  message: field.validation.message || msgs.maxLength(field.validation.maxLength),
                },
              }),
              ...(field.validation?.pattern && {
                pattern: {
                  value: new RegExp(field.validation.pattern),
                  message: field.validation.message || msgs.invalidFormat,
                },
              }),
            }}
            render={({ field: inputField }) => {
              // Values of options flagged `exclusive` (e.g. "None of the above"):
              // ticking one clears every other selection, and ticking any other
              // option clears them. Fields with no exclusive option are unaffected.
              const exclusiveValues = (field.options || [])
                .filter((o: any) => o.exclusive)
                .map((o: any) => o.value);

              return (
                <FormGroup>
                  {field.options?.map((option: any) => {
                    const selectedValues = inputField.value || [];
                    const isChecked = selectedValues.includes(option.value);

                    return (
                      <FormControlLabel
                        key={option.value}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={(e) => {
                              const currentValues = inputField.value || [];
                              let newValues;
                              if (e.target.checked) {
                                if (option.exclusive) {
                                  newValues = [option.value];
                                } else {
                                  newValues = [
                                    ...currentValues.filter(
                                      (v: any) => !exclusiveValues.includes(v)
                                    ),
                                    option.value,
                                  ];
                                }
                              } else {
                                newValues = currentValues.filter(
                                  (v: any) => v !== option.value
                                );
                              }
                              inputField.onChange(newValues);
                            }}
                            sx={{ color: '#3A7380' }}
                          />
                        }
                        label={option.label}
                        sx={{ mb: 0.5 }}
                      />
                    );
                  })}
                </FormGroup>
              );
            }}
          />
          {error && (
            <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
              {typeof error.message === 'string' ? error.message : msgs.required}
            </Typography>
          )}
        </Box>
      );

    case 'button-group': {
      const buttonHeight = (field.buttonHeight || 'medium') as 'small' | 'medium' | 'large';
      const heightStyles: Record<'small' | 'medium' | 'large', { padding: string; fontSize: string }> = {
        small: { padding: '6px 12px', fontSize: '0.85rem' },
        medium: { padding: '14px 20px', fontSize: '0.95rem' },
        large: { padding: '18px 24px', fontSize: '1rem' },
      };
      const currentHeightStyle = heightStyles[buttonHeight];
      // Sentence-length options overflow the default single-line row on desktop
      // (the row only wraps below 900px), so a field can opt into a stacked
      // layout: full-width buttons, wrapping labels, at every breakpoint.
      const isVerticalOptions = (field as any).optionsLayout === 'vertical';

      return (
        <Box
          key={key}
          data-field-name={field.name}
          sx={{
            mb: { xs: 2, sm: 2.5, md: 3 },
            // Add white bordered box styling on mobile - each button-group gets its own separate box
            ...(isMobile && {
              backgroundColor: 'white',
              borderRadius: '16px',
              border: '1px solid #E5E5E5',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              maxWidth: '700px',
              mx: 'auto',
              width: '100%',
              p: { xs: 1.5, sm: 2 },
            }),
          }}
        >
          {/* Show HTML Content if available, otherwise show Text Label */}
          {(field as any).htmlContent && (field as any).htmlContent.trim() !== '' && (field as any).htmlContent !== '<p><br></p>' ? (
            <Box
              sx={{
                mb: 2,
                color: '#3A7380',
                fontSize: '0.95rem',
                fontWeight: 500,
                lineHeight: 1.5,
                fontFamily: '"Open Sans", sans-serif',
                '& p': { mb: 1, mt: 0 },
                '& p:last-child': { mb: 0 },
                '& ul': { mb: 1, mt: 0.5, pl: 2.5, listStyleType: 'disc', display: 'block', marginLeft: 0 },
                '& ol': { mb: 1, mt: 0.5, pl: 2.5, listStyleType: 'decimal', display: 'block', marginLeft: 0 },
                '& li': { mb: 0.5, display: 'list-item' },
              }}
              dangerouslySetInnerHTML={{ __html: insertAsteriskBeforeList(substituteVariables(replaceScorePlaceholders((field as any).htmlContent, scores), variableValues || {}), field.required || false) }}
            />
          ) : (
            <Typography
              variant="body2"
              sx={{
                mb: 1.5,
                color: '#3A7380',
                fontSize: '0.95rem',
                fontWeight: 500,
                fontFamily: '"Open Sans", sans-serif',
              }}
            >
              {field.label}
              {field.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
          )}

          <Controller
            name={field.name}
            control={control}
            rules={{
              ...(field.required && { required: msgs.required }),
              ...(field.validation?.minLength && {
                minLength: {
                  value: field.validation.minLength,
                  message: field.validation.message || msgs.minLength(field.validation.minLength),
                },
              }),
              ...(field.validation?.maxLength && {
                maxLength: {
                  value: field.validation.maxLength,
                  message: field.validation.message || msgs.maxLength(field.validation.maxLength),
                },
              }),
              ...(field.validation?.pattern && {
                pattern: {
                  value: new RegExp(field.validation.pattern),
                  message: field.validation.message || msgs.invalidFormat,
                },
              }),
            }}
            render={({ field: inputField }) => (
              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  ...(isVerticalOptions
                    ? { flexDirection: 'column' }
                    : field.options && field.options.length > 3 && {
                        '@media (max-width: 899px)': {
                          flexWrap: 'wrap',
                          justifyContent: 'center',
                        },
                      }),
                }}
              >
                {field.options?.map((option: any) => {
                  const isSelected = inputField.value === option.value;
                  return (
                    <Button
                      key={option.value}
                      onClick={() => {
                        // Select the value first
                        inputField.onChange(option.value);

                        // Check if this option has a popup configured
                        if (option.popupId) {
                          // Set button-group popup context for reset functionality
                          onSetButtonGroupPopupContext({
                            fieldName: field.name,
                            resetOnBack: option.resetOnBack || false,
                          });
                          // Show the popup
                          onOpenPopup(option.popupId);
                        }
                      }}
                      sx={{
                        flex: 1,
                        minHeight: { xs: '40px', md: 'auto' },
                        borderRadius: '12px',
                        padding: currentHeightStyle.padding,
                        textTransform: 'none',
                        fontSize: currentHeightStyle.fontSize,
                        fontWeight: 500,
                        fontFamily: '"Open Sans", sans-serif',
                        whiteSpace: isVerticalOptions ? 'normal' : 'nowrap',
                        ...(isVerticalOptions && {
                          width: '100%',
                          flex: '0 0 auto',
                          textAlign: 'left',
                          justifyContent: 'flex-start',
                          lineHeight: 1.4,
                        }),
                        backgroundColor: isSelected ? '#3A7380' : 'white',
                        color: isSelected ? 'white' : '#3A7380',
                        border: `1px solid ${isSelected ? '#3A7380' : '#B4D8D6'}`,
                        '&:hover': {
                          backgroundColor: isSelected ? '#3A7380' : '#f7fafc',
                          borderColor: '#3A7380',
                        },
                        '@media (max-width: 899px)': {
                          whiteSpace: 'normal',
                          lineHeight: 1.3,
                          padding: '8px 12px',
                          fontSize: '0.9rem',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: isVerticalOptions ? 'flex-start' : 'center',
                          ...(!isVerticalOptions &&
                            field.options &&
                            field.options.length > 3 && {
                              width: 'calc(50% - 12px)',
                              flex: '0 0 auto',
                            }),
                        },
                      }}
                    >
                      {option.label}
                    </Button>
                  );
                })}
              </Box>
            )}
          />
          {field.helperText && (
            <FormHelperText sx={{ color: '#5A7380', mt: 1 }}>
              {field.helperText}
            </FormHelperText>
          )}
          {error && (
            <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
              {typeof error.message === 'string' ? error.message : msgs.required}
            </Typography>
          )}
        </Box>
      );
    }

    case 'date':
      return (
        <Box key={key} data-field-name={field.name} sx={{ mb: { xs: 1.75, sm: 2.5, md: 3 } }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: '#3A7380',
              fontSize: '0.95rem',
              fontWeight: 500,
              fontFamily: '"Open Sans", sans-serif',
            }}
          >
            {field.label}
            {field.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <Controller
            name={field.name}
            control={control}
            rules={{
              ...(field.required && { required: msgs.required }),
              ...(field.validation?.validationType && {
                validate: (value: string) => {
                  if (!value) return true;

                  const inputDate = new Date(value);

                  if (isNaN(inputDate.getTime())) {
                    return field.validation?.message || msgs.invalidDate;
                  }

                  const today = new Date();
                  today.setHours(0, 0, 0, 0);

                  switch (field.validation?.validationType) {
                    case 'pastDate':
                      if (inputDate >= today) {
                        return field.validation?.message || msgs.datePast;
                      }
                      break;

                    case 'futureDate':
                      if (inputDate <= today) {
                        return field.validation?.message || msgs.dateFuture;
                      }
                      break;

                    case 'dateWithinDays': {
                      const daysAgo = field.validation?.dateWithinDays || 14;
                      const minDate = new Date();
                      minDate.setDate(minDate.getDate() - daysAgo);
                      minDate.setHours(0, 0, 0, 0);

                      if (inputDate < minDate || inputDate > today) {
                        return field.validation?.message || msgs.dateWithinDays(daysAgo);
                      }
                      break;
                    }

                    case 'minAge': {
                      const minAge = field.validation?.minAge || 18;
                      const ageDate = new Date();
                      ageDate.setFullYear(ageDate.getFullYear() - minAge);

                      if (inputDate > ageDate) {
                        return field.validation?.message || msgs.minAge(minAge);
                      }
                      break;
                    }
                  }

                  return true;
                },
              }),
            }}
            render={({ field: inputField }) => (
              <TextField
                {...inputField}
                fullWidth
                type="date"
                variant="outlined"
                error={!!error}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: error ? '#d32f2f' : '#E0E0E0',
                    },
                    '&:hover fieldset': {
                      borderColor: error ? '#d32f2f' : '#3A7380',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: error ? '#d32f2f' : '#3A7380',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '14px 16px',
                    color: '#3A7380 !important',
                    '&::-webkit-datetime-edit': {
                      color: '#3A7380 !important',
                    },
                    '&::-webkit-datetime-edit-fields-wrapper': {
                      color: '#3A7380 !important',
                    },
                    '&::-webkit-datetime-edit-text': {
                      color: '#3A7380 !important',
                    },
                    '&::-webkit-datetime-edit-month-field': {
                      color: '#3A7380 !important',
                    },
                    '&::-webkit-datetime-edit-day-field': {
                      color: '#3A7380 !important',
                    },
                    '&::-webkit-datetime-edit-year-field': {
                      color: '#3A7380 !important',
                    },
                  },
                }}
              />
            )}
          />
          {error && (
            <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
              {typeof error.message === 'string' ? error.message : msgs.required}
            </Typography>
          )}
        </Box>
      );

    case 'number':
      return (
        <Box key={key} data-field-name={field.name} sx={{ mb: { xs: 1.75, sm: 2.5, md: 3 } }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: '#3A7380',
              fontSize: '0.95rem',
              fontWeight: 500,
              fontFamily: '"Open Sans", sans-serif',
            }}
          >
            {field.label}
            {field.required && <span style={{ color: 'red' }}>*</span>}
          </Typography>
          <Controller
            name={field.name}
            control={control}
            rules={{
              ...(field.required && { required: msgs.required }),
              ...(field.validation?.minLength && {
                minLength: {
                  value: field.validation.minLength,
                  message: field.validation.message || msgs.minLength(field.validation.minLength),
                },
              }),
              ...(field.validation?.maxLength && {
                maxLength: {
                  value: field.validation.maxLength,
                  message: field.validation.message || msgs.maxLength(field.validation.maxLength),
                },
              }),
              ...(field.validation?.pattern && {
                pattern: {
                  value: new RegExp(field.validation.pattern),
                  message: field.validation.message || msgs.invalidFormat,
                },
              }),
              ...(field.validation?.min !== undefined && field.validation?.min !== null && {
                min: {
                  value: field.validation.min,
                  message: field.validation.message || msgs.minValue(field.validation.min),
                },
              }),
              ...(field.validation?.max !== undefined && field.validation?.max !== null && {
                max: {
                  value: field.validation.max,
                  message: field.validation.message || msgs.maxValue(field.validation.max),
                },
              }),
            }}
            render={({ field: inputField }) => (
              <TextField
                {...inputField}
                fullWidth
                type="number"
                placeholder={field.placeholder}
                variant="outlined"
                error={!!error}
                inputProps={{
                  min: field.validation?.min,
                  max: field.validation?.max,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: error ? '#d32f2f' : '#E0E0E0',
                    },
                    '&:hover fieldset': {
                      borderColor: error ? '#d32f2f' : '#3A7380',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: error ? '#d32f2f' : '#3A7380',
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '14px 16px',
                  },
                }}
              />
            )}
          />
          {field.helperText && !error && (
            <FormHelperText sx={{ color: '#5A7380', mt: 0.5 }}>
              {field.helperText}
            </FormHelperText>
          )}
          {error && (
            <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
              {typeof error.message === 'string' ? error.message : msgs.required}
            </Typography>
          )}
        </Box>
      );

    case 'file': {
      const allowMultiple = field.fileConfig?.allowMultiple || false;
      const maxFilesAllowed = field.fileConfig?.maxFiles || 10;
      const maxFileSize = field.fileConfig?.maxFileSize || 10485760; // 10MB default

      const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
      };

      return (
        <Box key={key} data-field-name={field.name} sx={{ mb: { xs: 1.75, sm: 2.5, md: 3 } }}>
          <Controller
            name={field.name}
            control={control}
            rules={field.required ? { required: msgs.required } : {}}
            render={({ field: inputField }) => {
              const files = allowMultiple
                ? (Array.isArray(inputField.value) ? inputField.value : [])
                : (inputField.value ? [inputField.value] : []);

              const canUploadMore = allowMultiple ? files.length < maxFilesAllowed : files.length === 0;

              return (
                <>
                  {/* Outer Container */}
                  <Box
                    sx={{
                      border: '1px solid #E5F0F1',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      boxShadow: '0px 4px 4px 0px rgba(58, 115, 128, 0.2)',
                      p: { xs: 1.5, md: 2 },
                    }}
                  >
                    {/* Label Inside Container */}
                    <Typography
                      sx={{
                        color: '#3A7380',
                        fontWeight: 700,
                        fontSize: { xs: '16px', md: '20px' },
                        fontFamily: '"Open Sans", sans-serif',
                        mb: { xs: 1, md: 2 },
                      }}
                    >
                      {field.label}
                      {field.required && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                    </Typography>

                    {/* Inner Dotted Upload Box */}
                    <Box
                      component="label"
                      sx={{
                        display: 'block',
                        border: '2px dashed #B4D8D6',
                        borderRadius: '12px',
                        backgroundColor: '#FAFBFB',
                        p: { xs: 2, md: 4 },
                        mx: { xs: 0, md: 3 },
                        textAlign: 'center',
                        cursor: canUploadMore ? 'pointer' : 'not-allowed',
                        opacity: canUploadMore ? 1 : 0.6,
                        transition: 'all 0.2s',
                        '&:hover': canUploadMore ? {
                          borderColor: '#3A7380',
                          backgroundColor: '#F0F7F8',
                        } : {},
                      }}
                    >
                      <input
                        type="file"
                        hidden
                        multiple={allowMultiple}
                        disabled={!canUploadMore}
                        accept={field.fileConfig?.allowedFileTypes?.join(',') || '.pdf,.jpg,.jpeg,.png,.webp'}
                        onChange={async (e) => {
                          const selectedFiles = e.target.files;
                          if (!selectedFiles || selectedFiles.length === 0) return;

                          // Validate file size
                          for (let i = 0; i < selectedFiles.length; i++) {
                            if (selectedFiles[i].size > maxFileSize) {
                              alert(`File "${selectedFiles[i].name}" exceeds maximum size of ${formatFileSize(maxFileSize)}`);
                              e.target.value = '';
                              return;
                            }
                          }

                          if (allowMultiple) {
                            // Multiple files mode
                            const newFiles = Array.from(selectedFiles);
                            const totalFiles = files.length + newFiles.length;

                            if (totalFiles > maxFilesAllowed) {
                              alert(`You can upload up to ${maxFilesAllowed} files. You already have ${files.length} file(s).`);
                              e.target.value = '';
                              return;
                            }

                            // Upload files
                            if (token) {
                              // Public wizard: always upload via public endpoint
                              try {
                                const uploadedFiles = await Promise.all(
                                  newFiles.map(file =>
                                    fileService.uploadPublicFile(token, file, field.name)
                                  )
                                );
                                const fileMetadata = uploadedFiles.map(uploaded => ({
                                  id: uploaded.id,
                                  name: uploaded.file_name,
                                  size: uploaded.file_size
                                }));
                                inputField.onChange([...files, ...fileMetadata]);
                              } catch (uploadError) {
                                console.error('Error uploading files:', uploadError);
                                alert(msgs.uploadMultiFailed);
                              }
                            } else if (questionnaireId) {
                              // Generic wizard: upload via authenticated endpoint
                              try {
                                const uploadedFiles = await Promise.all(
                                  newFiles.map(file =>
                                    fileService.uploadFile(file, undefined, undefined, undefined, questionnaireId, field.name, field.fileConfig?.fileTypeId)
                                  )
                                );
                                const fileMetadata = uploadedFiles.map(uploaded => ({
                                  id: uploaded.id,
                                  name: uploaded.file_name,
                                  size: uploaded.file_size
                                }));
                                inputField.onChange([...files, ...fileMetadata]);
                              } catch (uploadError) {
                                console.error('Error uploading files:', uploadError);
                                alert(msgs.uploadMultiFailed);
                              }
                            } else {
                              // No token yet (public wizard before token is available), store File objects temporarily
                              inputField.onChange([...files, ...newFiles]);
                            }
                          } else {
                            // Single file mode
                            if (token) {
                              // Public wizard: upload via public endpoint
                              try {
                                const uploaded = await fileService.uploadPublicFile(token, selectedFiles[0], field.name);
                                inputField.onChange({
                                  id: uploaded.id,
                                  name: uploaded.file_name,
                                  size: uploaded.file_size
                                });
                              } catch (uploadError) {
                                console.error('Error uploading file:', uploadError);
                                alert(msgs.uploadFailed);
                              }
                            } else if (questionnaireId) {
                              // Generic wizard: upload via authenticated endpoint
                              try {
                                const uploaded = await fileService.uploadFile(selectedFiles[0], undefined, undefined, undefined, questionnaireId, field.name, field.fileConfig?.fileTypeId);
                                inputField.onChange({
                                  id: uploaded.id,
                                  name: uploaded.file_name,
                                  size: uploaded.file_size
                                });
                              } catch (uploadError) {
                                console.error('Error uploading file:', uploadError);
                                alert(msgs.uploadFailed);
                              }
                            } else {
                              // No token yet, store File object temporarily
                              inputField.onChange(selectedFiles[0]);
                            }
                          }

                          e.target.value = '';
                        }}
                      />

                      {/* Upload Icon */}
                      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                        <svg width="35" height="35" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M15.3125 26.25V8.42188L9.625 14.1094L6.5625 10.9375L17.5 0L28.4375 10.9375L25.375 14.1094L19.6875 8.42188V26.25H15.3125ZM4.375 35C3.17188 35 2.14229 34.572 1.28625 33.7159C0.430208 32.8599 0.00145833 31.8296 0 30.625V24.0625H4.375V30.625H30.625V24.0625H35V30.625C35 31.8281 34.572 32.8584 33.7159 33.7159C32.8599 34.5734 31.8296 35.0015 30.625 35H4.375Z" fill="#858282"/>
                        </svg>
                      </Box>

                      {/* Upload Text */}
                      <Typography
                        sx={{
                          color: '#B7B7B7',
                          fontSize: '14px',
                          fontWeight: 400,
                          fontFamily: '"Open Sans", sans-serif',
                          textAlign: 'center',
                        }}
                      >
                        Upload GP summary or other documents
                      </Typography>
                      <Typography
                        sx={{
                          color: '#B7B7B7',
                          fontSize: '14px',
                          fontWeight: 400,
                          fontFamily: '"Open Sans", sans-serif',
                          textAlign: 'center',
                        }}
                      >
                        Drag and drop or click to browse
                      </Typography>
                    </Box>

                    {/* Display uploaded files */}
                    {files.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        {allowMultiple && (
                          <Typography variant="caption" sx={{ color: '#5A7380', mb: 1, display: 'block' }}>
                            {files.length} / {maxFilesAllowed} file(s) uploaded
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {files.map((file: any, index: number) => (
                            <Box
                              key={index}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 1.5,
                                backgroundColor: '#F9FAFB',
                                borderRadius: '8px',
                                border: '1px solid #E5F0F1',
                              }}
                            >
                              <Box
                                sx={{
                                  flex: 1,
                                  minWidth: 0,
                                  cursor: (file instanceof File || file.id) ? 'pointer' : 'default',
                                  '&:hover': (file instanceof File || file.id) ? {
                                    '& .MuiTypography-root:first-of-type': {
                                      textDecoration: 'underline',
                                    }
                                  } : {}
                                }}
                                onClick={async () => {
                                  if (file instanceof File) {
                                    const url = URL.createObjectURL(file);
                                    window.open(url, '_blank');
                                    setTimeout(() => URL.revokeObjectURL(url), 100);
                                  } else if (file.id) {
                                    try {
                                      await fileService.downloadFile(file.id);
                                    } catch (downloadError) {
                                      console.error('Error downloading file:', downloadError);
                                      alert(msgs.downloadFailed);
                                    }
                                  }
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#3A7380',
                                    fontFamily: '"Open Sans", sans-serif',
                                    fontWeight: 500,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {file.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#5A7380',
                                    fontFamily: '"Open Sans", sans-serif',
                                  }}
                                >
                                  {formatFileSize(file.size || 0)}
                                </Typography>
                              </Box>
                              <Button
                                size="small"
                                onClick={() => {
                                  if (allowMultiple) {
                                    const newFiles = files.filter((_: any, i: number) => i !== index);
                                    inputField.onChange(newFiles.length > 0 ? newFiles : []);
                                  } else {
                                    inputField.onChange(null);
                                  }
                                }}
                                sx={{
                                  color: '#d32f2f',
                                  minWidth: 'auto',
                                  fontSize: '0.875rem',
                                  fontFamily: '"Open Sans", sans-serif',
                                  '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                  },
                                }}
                              >
                                Remove
                              </Button>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </>
              );
            }}
          />
          {error && (
            <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
              {typeof error.message === 'string' ? error.message : msgs.required}
            </Typography>
          )}
        </Box>
      );
    }

    case 'button': {
      const isExitButton = field.action === 'exit';

      return (
        <Box
          key={key}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Button
            variant={isExitButton ? 'outlined' : 'contained'}
            onClick={async () => {
              setActionClicked(true);
              if (field.action === 'open-popup' && field.popupId) {
                if (shouldValidateBeforePopup && currentSectionFields) {
                  // Validate current section before opening popup (public wizard behaviour)
                  const fieldsToValidate = currentSectionFields
                    .filter((f: any) => f.type !== 'button' && f.type !== 'content' && f.type !== 'info-banner')
                    .map((f: any) => f.name);
                  const isValid = await onTriggerValidation(fieldsToValidate);
                  if (!isValid) {
                    onScrollToFirstError?.();
                    return;
                  }
                }
                onOpenPopup(field.popupId);
              } else if (field.action === 'submit') {
                onSubmit();
              } else if (field.action === 'exit') {
                // Try exit popup first, fall back to direct exit
                if (!onTriggerExitPopup()) {
                  // Exit popup not shown, redirect directly if exitUrl exists (public wizard)
                  onExit(field.exitUrl);
                }
              } else if (field.action === 'next-and-score') {
                // Next and Score: save, calculate score, optionally show popup, then navigate
                const shouldShowPopup = !!field.popupId;
                const success = await onNext({
                  shouldCalculateScore: !!field.scoreName,
                  scoreName: field.scoreName,
                  skipNavigation: shouldShowPopup,
                });

                if (success && shouldShowPopup) {
                  // Show popup after score calculation
                  onSetNavigateAfterPopup(true);
                  onOpenPopup(field.popupId);
                }
              } else if (field.action === 'pagination-next') {
                // Pagination Continue button: save current page and move to next page
                await onSaveCurrentPage();
              } else {
                // Regular next button
                if (isMobile && hasMorePages()) {
                  // Mobile pagination: validate and advance to next page
                  await onMobileNextPage();
                } else {
                  // Desktop or last page: normal navigation to next section
                  await onNext();
                }
              }
            }}
            disabled={saving}
            sx={{
              ...(isExitButton ? {
                // Exit button: secondary outlined style
                borderColor: '#B0B0B0',
                color: '#616161',
                backgroundColor: 'transparent',
                borderRadius: '10px',
                minWidth: '100px',
                height: '40px',
                paddingX: '16px',
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 500,
                fontFamily: '"Open Sans", sans-serif',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderColor: '#9E9E9E',
                },
                '&:disabled': {
                  borderColor: '#E0E0E0',
                  color: '#BDBDBD',
                },
              } : {
                // Primary button: original teal style
                backgroundColor: '#3A7380',
                color: 'white',
                borderRadius: '10px',
                minWidth: '120px',
                height: '40px',
                paddingX: '20px',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                letterSpacing: '0.05em',
                boxShadow: 'none',
                fontFamily: '"Open Sans", sans-serif',
                whiteSpace: 'nowrap',
                '&:hover': {
                  backgroundColor: '#2e5c66',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  backgroundColor: '#B0B0B0',
                },
              }),
            }}
          >
            {saving && actionClicked && (
              <CircularProgress size={16} thickness={5} sx={{ color: 'inherit', marginInlineEnd: 1 }} />
            )}
            {field.label}
          </Button>
          {field.helperText && (
            <FormHelperText
              sx={{
                textAlign: 'center',
                color: isExitButton ? '#757575' : '#3A7380',
                fontSize: '14px',
                fontWeight: 400,
                fontFamily: '"Open Sans", sans-serif',
                mt: 1,
              }}
            >
              {field.helperText}
            </FormHelperText>
          )}
        </Box>
      );
    }

    case 'content': {
      const areaType = (field as any).areaType || 'box';
      const listStyle = (field as any).listStyle || 'default';
      const customIconsArray = (field as any).customIcons || [];
      const rawContent = (field as any).content || '';
      let htmlContent = substituteVariables(replaceScorePlaceholders(rawContent, scores), variableValues || {});

      // Inject custom icons if listStyle is 'custom-icons'
      if (listStyle === 'custom-icons' && customIconsArray.length > 0) {
        htmlContent = injectCustomIcons(htmlContent, customIconsArray);
      }

      // Links inside content should open in a new tab so the user doesn't navigate
      // away from (and lose progress in) the questionnaire.
      htmlContent = htmlContent.replace(/<a\s(?![^>]*\btarget=)/gi, '<a target="_blank" rel="noopener noreferrer" ');

      const listStyles = getListStyles(listStyle);
      // Render anchors as recognisable links (blue + underlined) — the surrounding
      // content colour would otherwise make them look like plain text.
      const linkStyles = {
        '& a': { color: '#1A73E8', textDecoration: 'underline' },
        '& a:hover': { color: '#1558B0' },
      } as const;

      // Summary Box rendering
      if (areaType === 'summary-box') {
        const summaryData = (field as any).summaryData || { header: '', rows: [] };

        return (
          <Box
            key={key}
            sx={{
              backgroundColor: 'white',
              border: '1px solid #F5F9FA',
              boxShadow: '0px 4px 4px 0px #3A7380',
              borderRadius: '12px',
              p: 1.5,
              mb: 3,
              color: '#3A7380',
              fontSize: '0.95rem',
              lineHeight: 1.3,
              fontFamily: '"Open Sans", sans-serif',
            }}
          >
            {summaryData.header && (
              <Typography
                sx={{
                  color: '#3A7380',
                  fontSize: '0.95rem',
                  mb: 1.5,
                  fontFamily: '"Open Sans", sans-serif',
                }}
              >
                {summaryData.header}
              </Typography>
            )}
            {summaryData.rows && summaryData.rows.length > 0 && (
              <Box>
                {summaryData.rows.map((row: any, idx: number) => {
                  // Determine the value to display
                  let displayValue = row.value;
                  if (row.valueType === 'displayAmount') {
                    // Prefer the questionnaire's configured payment amount; fall back to
                    // the live payment-intent amount. Both are in pence.
                    const amountInPence = (paymentConfigAmount && paymentConfigAmount > 0)
                      ? paymentConfigAmount
                      : paymentAmount;
                    const displayAmountValue = amountInPence > 0 ? (amountInPence / 100).toFixed(2) : '0.00';
                    displayValue = `£${displayAmountValue}`;
                  }

                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1.5,
                        borderTop: idx === 0 ? '1px solid #E0E0E0' : 'none',
                        borderBottom: idx < summaryData.rows.length - 1 ? '1px solid #E0E0E0' : 'none',
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#3A7380',
                          fontSize: '0.95rem',
                          fontFamily: '"Open Sans", sans-serif',
                        }}
                      >
                        {row.label}
                      </Typography>
                      <Typography
                        sx={{
                          color: '#3A7380',
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          fontFamily: '"Open Sans", sans-serif',
                        }}
                      >
                        {displayValue}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
            {paymentDiscountText && (
              <Typography
                sx={{
                  color: '#3A7380',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  fontFamily: '"Open Sans", sans-serif',
                  pt: 1.5,
                  borderTop: '1px solid #E0E0E0',
                }}
              >
                {paymentDiscountText}
              </Typography>
            )}
          </Box>
        );
      } else if (areaType === 'box') {
        return (
          <Box
            key={key}
            sx={{
              backgroundColor: 'white',
              border: '1px solid #F5F9FA',
              boxShadow: '0px 4px 4px 0px #3A7380',
              borderRadius: '12px',
              p: 1.5,
              mb: 3,
              color: '#3A7380',
              fontSize: '0.95rem',
              lineHeight: 1.3,
              fontFamily: '"Open Sans", sans-serif',
              '& p': { mb: 1, mt: 0 },
              '& p:last-child': { mb: 0 },
              ...listStyles,
              ...linkStyles,
              '& .ql-align-center': { textAlign: 'center' },
              '& .ql-align-right': { textAlign: 'right' },
              '& .ql-align-justify': { textAlign: 'justify' },
            }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        );
      } else if (areaType === 'filled') {
        return (
          <Box
            key={key}
            sx={{
              backgroundColor: '#E5F0F1',
              borderRadius: '12px',
              p: 1.5,
              mb: 3,
              color: '#3A7380',
              fontSize: '0.95rem',
              lineHeight: 1.3,
              fontFamily: '"Open Sans", sans-serif',
              '& p': { mb: 1, mt: 0 },
              '& p:last-child': { mb: 0 },
              ...listStyles,
              ...linkStyles,
              '& .ql-align-center': { textAlign: 'center' },
              '& .ql-align-right': { textAlign: 'right' },
              '& .ql-align-justify': { textAlign: 'justify' },
            }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />
        );
      } else {
        return (
          <Box key={key} data-field-name={field.name} sx={{ mb: { xs: 1.75, sm: 2.5, md: 3 } }}>
            {htmlContent && (
              <Box
                sx={{
                  color: '#3A7380',
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                  fontFamily: '"Open Sans", sans-serif',
                  mb: 2,
                  '& p': { mb: 1 },
                  ...listStyles,
                  ...linkStyles,
                  '& .ql-align-center': { textAlign: 'center' },
                  '& .ql-align-right': { textAlign: 'right' },
                  '& .ql-align-justify': { textAlign: 'justify' },
                }}
                dangerouslySetInnerHTML={{ __html: htmlContent }}
              />
            )}
          </Box>
        );
      }
    }

    case 'info-banner': {
      const scoreTitle = (field as any).scoreTitle || '';
      const scoreLevelText = (field as any).scoreNameToDisplay || '';

      return (
        <Box
          key={key}
          sx={{
            backgroundColor: '#3AA09A7A',
            borderRadius: '12px',
            py: 2,
            px: 3,
            mb: 3,
            maxWidth: '450px',
            mx: 'auto',
            textAlign: 'center',
            boxShadow: '0px 4px 4px 0px #3A7380',
          }}
        >
          <Typography
            sx={{
              color: '#3A7380',
              fontSize: '1rem',
              fontWeight: 700,
              fontFamily: '"Open Sans", sans-serif',
              mb: 2,
            }}
          >
            {scoreTitle}
          </Typography>
          <Typography
            sx={{
              color: '#3A7380',
              fontSize: '2rem',
              fontWeight: 400,
              fontFamily: '"Open Sans", sans-serif',
            }}
          >
            {scoreLevelText}
          </Typography>
        </Box>
      );
    }

    case 'line': {
      const nestedFields = (field as any).nestedFields || [];
      if (nestedFields.length === 0) {
        return null;
      }

      return (
        <Box
          key={key}
          sx={{
            mb: 3,
            display: 'flex',
            gap: { xs: 0, md: 2 },
            flexWrap: 'wrap',
          }}
        >
          {nestedFields.map((nestedField: any) => (
            <Box
              key={nestedField.name}
              sx={{
                flex: '1 1 0',
                minWidth: '200px',
              }}
            >
              {renderNested(nestedField)}
            </Box>
          ))}
        </Box>
      );
    }

    case 'container': {
      const containerNestedFields = (field as any).nestedFields || [];
      if (containerNestedFields.length === 0) {
        return null;
      }

      return (
        <Box
          key={key}
          sx={{
            mb: 3,
          }}
        >
          {/* Container Box - with title inside */}
          <Box
            sx={{
              border: '1px solid #E5F0F1',
              borderRadius: '12px',
              backgroundColor: 'white',
              boxShadow: '0px 4px 4px 0px rgba(58, 115, 128, 0.2)',
              p: 1.5,
            }}
          >
            {/* Container Label/Title inside the box */}
            {field.label && (
              <Typography
                sx={{
                  color: '#3A7380',
                  fontWeight: 700,
                  fontSize: '20px',
                  fontFamily: '"Open Sans", sans-serif',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  mb: 1.5,
                }}
              >
                {field.label}
              </Typography>
            )}
            {/* Fields stacked vertically with no gap */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {containerNestedFields.map((nestedField: any) => {
                // Check if this nested field is a 'line' field
                if (nestedField.type === 'line') {
                  const lineNestedFields = nestedField.nestedFields || [];
                  if (lineNestedFields.length === 0) {
                    return null;
                  }
                  // Render line fields horizontally as a row
                  return (
                    <Box
                      key={nestedField.name}
                      sx={{
                        display: 'flex',
                        gap: { xs: 0, md: 2 },
                        flexWrap: 'wrap',
                        mb: 1,
                      }}
                    >
                      {lineNestedFields.map((lineField: any) => (
                        <Box
                          key={lineField.name}
                          sx={{
                            flex: '1 1 0',
                            minWidth: '200px',
                            '& label': {
                              fontFamily: '"Open Sans", sans-serif',
                              fontWeight: 400,
                              fontSize: '20px',
                              lineHeight: '100%',
                              letterSpacing: '0%',
                              color: '#3A7380',
                              mb: 0.5,
                              display: 'block',
                            },
                            '& .MuiTextField-root': {
                              mb: 0,
                            },
                            '& .MuiOutlinedInput-root': {
                              height: '40px',
                              minHeight: '40px',
                            },
                            '& .MuiOutlinedInput-input': {
                              padding: '8px 12px',
                            },
                          }}
                        >
                          {renderNested(lineField)}
                        </Box>
                      ))}
                    </Box>
                  );
                }

                // Render normal fields vertically
                return (
                  <Box
                    key={nestedField.name}
                    sx={{
                      '& > .MuiBox-root': {
                        mb: 0,
                      },
                      '& label': {
                        fontFamily: '"Open Sans", sans-serif',
                        fontWeight: 400,
                        fontSize: '20px',
                        lineHeight: '100%',
                        letterSpacing: '0%',
                        color: '#3A7380',
                        mb: 0.5,
                        display: 'block',
                      },
                      '& .MuiTextField-root': {
                        mb: 1,
                      },
                      '& .MuiOutlinedInput-root': {
                        height: '40px',
                        minHeight: '40px',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 12px',
                      },
                    }}
                  >
                    {renderNested(nestedField)}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      );
    }

    case 'stripe_cc':
      return (
        <Box key={key} data-field-name={field.name} sx={{ mb: { xs: 1.75, sm: 2.5, md: 3 } }}>
          {token && stripeCCFieldRef && (
            <StripeCCField
              ref={stripeCCFieldRef}
              questionnaireToken={token}
              onPaymentReady={onPaymentReady || (() => {})}
              onPaymentSuccess={onPaymentSuccess || (() => {})}
              buttonLabel={paymentButtonLabel}
            />
          )}
          {!token && (
            <Alert severity="info" sx={{ my: 2 }}>
              {msgs.completePrevious}
            </Alert>
          )}
        </Box>
      );

    default:
      return null;
  }
};

export default WizardRenderField;
